import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, Plus, Pencil, Trash2, X, Save, ChevronDown, ChevronUp,
  BookOpen, GripVertical, Target, FileText, Image as ImageIcon
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  'Bureautique',
  'Finances / Comptabilité',
  'Ressources Humaines',
  'Management & Développement'
];

const LEVELS = ['Débutant', 'Intermédiaire', 'Avancé'];

const EMPTY_FORM = {
  title: '',
  category: CATEGORIES[0],
  level: 'Intermédiaire',
  duration: '8h',
  sessions: '',
  modality: 'Presentiel',
  type: 'En individuel ou en groupe (intra/inter)',
  description: '',
  objectives: [''],
  program: [''],
  image: '',
};

const AdminCatalogTab = ({ token }) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = list, 'new' = create, catalogId = edit
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/catalog`, { headers });
      setCatalog(res.data);
    } catch {
      toast.error('Erreur chargement du catalogue');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, objectives: [''], program: [''] });
    setEditingId('new');
  };

  const openEdit = async (catalogId) => {
    try {
      const res = await axios.get(`${API}/admin/catalog/${catalogId}`, { headers });
      const data = res.data;
      setForm({
        title: data.title || '',
        category: data.category || CATEGORIES[0],
        level: data.level || 'Intermédiaire',
        duration: data.duration || '8h',
        sessions: data.sessions || '',
        modality: data.modality || 'Presentiel',
        type: data.type || '',
        description: data.description || '',
        objectives: data.objectives?.length ? data.objectives : [''],
        program: data.program?.length ? data.program : [''],
        image: data.image || '',
      });
      setEditingId(catalogId);
    } catch {
      toast.error('Erreur chargement de la fiche');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    if (form.program.filter(p => p.trim()).length === 0) { toast.error('Au moins un module est requis'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        objectives: form.objectives.filter(o => o.trim()),
        program: form.program.filter(p => p.trim()),
      };

      if (editingId === 'new') {
        await axios.post(`${API}/admin/catalog`, payload, { headers });
        toast.success('Fiche catalogue créée !');
      } else {
        await axios.put(`${API}/admin/catalog/${editingId}`, payload, { headers });
        toast.success('Fiche catalogue mise à jour !');
      }
      setEditingId(null);
      fetchCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (catalogId, title) => {
    if (!window.confirm(`Supprimer la fiche "${title}" du catalogue ? Cette action est irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/catalog/${catalogId}`, { headers });
      toast.success('Fiche supprimee');
      fetchCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Dynamic list helpers
  const addListItem = (field) => {
    setForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeListItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateListItem = (field, index, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const moveListItem = (field, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= form[field].length) return;
    setForm(prev => {
      const arr = [...prev[field]];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return { ...prev, [field]: arr };
    });
  };

  const categories = [...new Set(catalog.map(c => c.category))].sort();
  const filtered = filterCat === 'all' ? catalog : catalog.filter(c => c.category === filterCat);

  // ============== FORM VIEW ==============
  if (editingId !== null) {
    return (
      <div data-testid="catalog-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">
            {editingId === 'new' ? 'Nouvelle fiche catalogue' : 'Modifier la fiche'}
          </h2>
          <Button variant="ghost" onClick={() => setEditingId(null)} data-testid="btn-cancel-form">
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Basic info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-800 text-sm mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-600" /> Informations generales
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Titre de la formation *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Gestion de la Paie"
                  required
                  data-testid="catalog-input-title"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Categorie *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="catalog-select-category"
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Niveau *</label>
                <select
                  value={form.level}
                  onChange={(e) => setForm({ ...form, level: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="catalog-select-level"
                >
                  {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Duree</label>
                <Input
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  placeholder="Ex: 16h"
                  data-testid="catalog-input-duration"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sessions</label>
                <Input
                  value={form.sessions}
                  onChange={(e) => setForm({ ...form, sessions: e.target.value })}
                  placeholder="Ex: 4 sessions de 4h"
                  data-testid="catalog-input-sessions"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Modalite</label>
                <Input
                  value={form.modality}
                  onChange={(e) => setForm({ ...form, modality: e.target.value })}
                  placeholder="Ex: Presentiel"
                  data-testid="catalog-input-modality"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                <Input
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  placeholder="Ex: En individuel ou en groupe"
                  data-testid="catalog-input-type"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Description courte de la formation..."
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[70px] resize-y"
                  data-testid="catalog-input-description"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">URL de l'image</label>
                <Input
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                  data-testid="catalog-input-image"
                />
                {form.image && (
                  <img src={form.image} alt="Aperçu" className="mt-2 w-32 h-20 object-cover rounded-lg border" />
                )}
              </div>
            </div>
          </div>

          {/* Programme (modules) */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-teal-600" /> Programme (modules) *
              </h3>
              <span className="text-xs text-slate-500">{form.program.filter(p => p.trim()).length} module(s)</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">Chaque module correspondra à une lecon dans le cours e-learning généré par IA.</p>
            <div className="space-y-2">
              {form.program.map((item, i) => (
                <div key={i} className="flex items-center gap-2 group" data-testid={`program-item-${i}`}>
                  <span className="text-xs text-slate-400 w-6 text-right shrink-0">{i + 1}.</span>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => moveListItem('program', i, -1)}
                      className="p-1 text-slate-300 hover:text-slate-500 disabled:opacity-30" disabled={i === 0}>
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => moveListItem('program', i, 1)}
                      className="p-1 text-slate-300 hover:text-slate-500 disabled:opacity-30" disabled={i === form.program.length - 1}>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Input
                    value={item}
                    onChange={(e) => updateListItem('program', i, e.target.value)}
                    placeholder={`Module ${i + 1}`}
                    className="flex-1"
                    data-testid={`program-input-${i}`}
                  />
                  <button type="button" onClick={() => removeListItem('program', i)}
                    className="p-1.5 text-slate-300 hover:text-red-500 transition-colors"
                    disabled={form.program.length <= 1}
                    data-testid={`program-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => addListItem('program')}
              className="mt-3 text-xs" data-testid="btn-add-program">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un module
            </Button>
          </div>

          {/* Objectifs */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-teal-600" /> Objectifs pedagogiques
              </h3>
              <span className="text-xs text-slate-500">{form.objectives.filter(o => o.trim()).length} objectif(s)</span>
            </div>
            <div className="space-y-2">
              {form.objectives.map((item, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`objective-item-${i}`}>
                  <span className="text-xs text-slate-400 w-6 text-right shrink-0">{i + 1}.</span>
                  <Input
                    value={item}
                    onChange={(e) => updateListItem('objectives', i, e.target.value)}
                    placeholder={`Objectif ${i + 1}`}
                    className="flex-1"
                    data-testid={`objective-input-${i}`}
                  />
                  <button type="button" onClick={() => removeListItem('objectives', i)}
                    className="p-1.5 text-slate-300 hover:text-red-500 transition-colors"
                    disabled={form.objectives.length <= 1}
                    data-testid={`objective-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => addListItem('objectives')}
              className="mt-3 text-xs" data-testid="btn-add-objective">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un objectif
            </Button>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => setEditingId(null)} data-testid="btn-cancel-save">
              Annuler
            </Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-save-catalog">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId === 'new' ? 'Créer la fiche' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // ============== LIST VIEW ==============
  return (
    <div data-testid="catalog-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
            data-testid="catalog-filter-category"
          >
            <option value="all">Toutes les categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-sm text-slate-500">{filtered.length} fiche(s)</span>
        </div>
        <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-new-catalog">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle fiche
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune fiche catalogue</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-semibold text-slate-600">Formation</th>
                <th className="text-left p-3 font-semibold text-slate-600 w-48">Categorie</th>
                <th className="text-left p-3 font-semibold text-slate-600 w-24">Niveau</th>
                <th className="text-left p-3 font-semibold text-slate-600 w-20">Duree</th>
                <th className="text-center p-3 font-semibold text-slate-600 w-20">Modules</th>
                <th className="text-right p-3 font-semibold text-slate-600 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr key={entry.catalog_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`catalog-row-${entry.catalog_id}`}>
                  <td className="p-3">
                    <p className="font-medium text-slate-900">{entry.title}</p>
                    {entry.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{entry.description}</p>
                    )}
                  </td>
                  <td className="p-3 text-slate-600 text-xs">{entry.category}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      entry.level === 'Débutant' ? 'bg-green-100 text-green-700' :
                      entry.level === 'Intermédiaire' ? 'bg-blue-100 text-blue-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {entry.level}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600 text-xs">{entry.duration}</td>
                  <td className="p-3 text-center">
                    <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                      {entry.program?.length || 0}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(entry.catalog_id)}
                        className="text-xs h-8" data-testid={`btn-edit-catalog-${entry.catalog_id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Editer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(entry.catalog_id, entry.title)}
                        className="text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`btn-delete-catalog-${entry.catalog_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCatalogTab;
